import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Flex,
  Grid,
  GridItem,
  Text,
  Textarea,
  Select,
  ButtonGroup,
  Badge,
  VStack,
  HStack,
  Divider,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue,
  useToast,
  IconButton,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Input,
  FormControl,
  FormLabel,
  Switch,
  useDisclosure,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spacer,
} from '@chakra-ui/react';
import { 
  FaSave, 
  FaDownload, 
  FaCamera, 
  FaEdit, 
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaQuestionCircle,
  FaHeart,
  FaLungs,
  FaBrain,
  FaEye,
  FaHandPaper
} from 'react-icons/fa';

const InteractivePatientAssessment = ({ 
  isOpen, 
  onClose, 
  patientEncounter, 
  onSave, 
  onExport,
  isReadOnly = false 
}) => {
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  
  // Assessment data state
  const [assessmentData, setAssessmentData] = useState({
    heent: {
      status: 'not_assessed',
      findings: '',
      severity: 'normal',
      details: {
        eyes: 'normal',
        ears: 'normal',
        nose: 'normal',
        throat: 'normal'
      }
    },
    neck: {
      status: 'not_assessed',
      findings: '',
      severity: 'normal',
      details: {
        mobility: 'normal',
        pain: 'none',
        swelling: 'none'
      }
    },
    chest: {
      status: 'not_assessed',
      findings: '',
      severity: 'normal',
      details: {
        heartSounds: 'normal',
        lungSounds: 'normal',
        breathing: 'normal'
      }
    },
    abdomen: {
      status: 'not_assessed',
      findings: '',
      severity: 'normal',
      details: {
        quadrants: {
          ruq: 'normal',
          luq: 'normal',
          rlq: 'normal',
          llq: 'normal'
        },
        bowelSounds: 'normal'
      }
    },
    extremities: {
      status: 'not_assessed',
      findings: '',
      severity: 'normal',
      details: {
        arms: 'normal',
        legs: 'normal',
        pulses: 'normal'
      }
    },
    neurological: {
      status: 'not_assessed',
      findings: '',
      severity: 'normal',
      details: {
        mentalStatus: 'normal',
        motorFunction: 'normal',
        sensoryFunction: 'normal'
      }
    },
    skin: {
      status: 'not_assessed',
      findings: '',
      severity: 'normal',
      details: {
        color: 'normal',
        temperature: 'normal',
        moisture: 'normal'
      }
    }
  });

  const [selectedRegion, setSelectedRegion] = useState('heent');
  const [vitalSigns, setVitalSigns] = useState({
    heartRate: '',
    bloodPressure: '',
    respiratoryRate: '',
    temperature: '',
    oxygenSaturation: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimeoutRef = useRef(null);

  // Status color mapping
  const statusColors = {
    normal: 'green.500',
    abnormal: 'red.500',
    not_assessed: 'gray.400'
  };

  const severityColors = {
    normal: 'green.500',
    mild: 'yellow.500',
    moderate: 'orange.500',
    severe: 'red.500'
  };

  // Update assessment data
  const updateAssessmentData = useCallback((region, field, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [region]: {
        ...prev[region],
        [field]: value
      }
    }));
  }, []);

  const updateAssessmentDetails = useCallback((region, detail, value) => {
    setAssessmentData(prev => ({
      ...prev,
      [region]: {
        ...prev[region],
        details: {
          ...prev[region].details,
          [detail]: value
        }
      }
    }));
  }, []);

  // Auto-save functionality
  const autoSave = useCallback(async () => {
    if (!patientEncounter) return;
    
    try {
      setIsSaving(true);
      const mappedData = {
        heentAssessment: { markdown: assessmentData.heent.findings },
        neckBackAssessment: { markdown: assessmentData.neck.findings },
        chestAssessment: { markdown: assessmentData.chest.findings },
        abdomenAssessment: { markdown: assessmentData.abdomen.findings },
        extremitiesAssessment: { markdown: assessmentData.extremities.findings },
        neurologicalAssessment: { markdown: assessmentData.neurological.findings },
        skinAssessment: { markdown: assessmentData.skin.findings },
        heartSounds: { markdown: assessmentData.chest.details.heartSounds },
        lungSounds: { markdown: assessmentData.chest.details.lungSounds },
        mentalStatus: { markdown: assessmentData.neurological.details.mentalStatus },
        heartRate: parseInt(vitalSigns.heartRate) || null,
        systolicBP: parseInt(vitalSigns.bloodPressure?.split('/')[0]) || null,
        diastolicBP: parseInt(vitalSigns.bloodPressure?.split('/')[1]) || null,
        respiratoryRate: parseInt(vitalSigns.respiratoryRate) || null,
        temperature: parseFloat(vitalSigns.temperature) || null,
        oxygenSaturation: parseInt(vitalSigns.oxygenSaturation) || null
      };

      await onSave(mappedData);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setIsSaving(false);
    }
  }, [assessmentData, vitalSigns, onSave, patientEncounter]);

  useEffect(() => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    
    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSave();
    }, 3000);
    
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [autoSave]);

  // Save assessment data
  const handleSave = useCallback(async () => {
    try {
      // Map assessment data to FISDAP fields
      const mappedData = {
        heentAssessment: { markdown: assessmentData.heent.findings },
        neckBackAssessment: { markdown: assessmentData.neck.findings },
        chestAssessment: { markdown: assessmentData.chest.findings },
        abdomenAssessment: { markdown: assessmentData.abdomen.findings },
        extremitiesAssessment: { markdown: assessmentData.extremities.findings },
        neurologicalAssessment: { markdown: assessmentData.neurological.findings },
        skinAssessment: { markdown: assessmentData.skin.findings },
        heartSounds: { markdown: assessmentData.chest.details.heartSounds },
        lungSounds: { markdown: assessmentData.chest.details.lungSounds },
        mentalStatus: { markdown: assessmentData.neurological.details.mentalStatus },
        // Map vital signs
        heartRate: parseInt(vitalSigns.heartRate) || null,
        systolicBP: parseInt(vitalSigns.bloodPressure?.split('/')[0]) || null,
        diastolicBP: parseInt(vitalSigns.bloodPressure?.split('/')[1]) || null,
        respiratoryRate: parseInt(vitalSigns.respiratoryRate) || null,
        temperature: parseFloat(vitalSigns.temperature) || null,
        oxygenSaturation: parseInt(vitalSigns.oxygenSaturation) || null
      };

      await onSave(mappedData);
      toast({
        title: 'Assessment Saved',
        description: 'Patient assessment data has been saved successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: 'Failed to save assessment data. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [assessmentData, vitalSigns, onSave, toast]);

  // Export assessment data
  const handleExport = useCallback(() => {
    const exportData = {
      patientEncounter: patientEncounter?.id,
      assessmentData,
      vitalSigns,
      timestamp: new Date().toISOString()
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `patient_assessment_${patientEncounter?.id || 'unknown'}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();

    toast({
      title: 'Assessment Exported',
      description: 'Assessment data has been exported successfully.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  }, [assessmentData, vitalSigns, patientEncounter, toast]);

  // Human body SVG component
  const HumanBodyDiagram = () => (
    <Box w="100%" h="400px" position="relative">
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 300 400"
        xmlns="http://www.w3.org/2000/svg"
        style={{ border: '1px solid #e2e8f0', borderRadius: '8px' }}
      >
        {/* Head */}
        <ellipse
          cx="150"
          cy="50"
          rx="40"
          ry="30"
          fill={assessmentData.heent.status === 'normal' ? '#48bb78' : 
                assessmentData.heent.status === 'abnormal' ? '#f56565' : '#a0aec0'}
          stroke="#2d3748"
          strokeWidth="2"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedRegion('heent')}
        />
        
        {/* Neck */}
        <rect
          x="135"
          y="75"
          width="30"
          height="25"
          fill={assessmentData.neck.status === 'normal' ? '#48bb78' : 
                assessmentData.neck.status === 'abnormal' ? '#f56565' : '#a0aec0'}
          stroke="#2d3748"
          strokeWidth="2"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedRegion('neck')}
        />
        
        {/* Chest/Torso */}
        <rect
          x="100"
          y="100"
          width="100"
          height="120"
          rx="10"
          fill={assessmentData.chest.status === 'normal' ? '#48bb78' : 
                assessmentData.chest.status === 'abnormal' ? '#f56565' : '#a0aec0'}
          stroke="#2d3748"
          strokeWidth="2"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedRegion('chest')}
        />
        
        {/* Abdomen */}
        <rect
          x="110"
          y="220"
          width="80"
          height="60"
          rx="5"
          fill={assessmentData.abdomen.status === 'normal' ? '#48bb78' : 
                assessmentData.abdomen.status === 'abnormal' ? '#f56565' : '#a0aec0'}
          stroke="#2d3748"
          strokeWidth="2"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedRegion('abdomen')}
        />
        
        {/* Arms */}
        <rect
          x="60"
          y="110"
          width="30"
          height="80"
          rx="15"
          fill={assessmentData.extremities.status === 'normal' ? '#48bb78' : 
                assessmentData.extremities.status === 'abnormal' ? '#f56565' : '#a0aec0'}
          stroke="#2d3748"
          strokeWidth="2"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedRegion('extremities')}
        />
        <rect
          x="210"
          y="110"
          width="30"
          height="80"
          rx="15"
          fill={assessmentData.extremities.status === 'normal' ? '#48bb78' : 
                assessmentData.extremities.status === 'abnormal' ? '#f56565' : '#a0aec0'}
          stroke="#2d3748"
          strokeWidth="2"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedRegion('extremities')}
        />
        
        {/* Legs */}
        <rect
          x="120"
          y="280"
          width="25"
          height="100"
          rx="12"
          fill={assessmentData.extremities.status === 'normal' ? '#48bb78' : 
                assessmentData.extremities.status === 'abnormal' ? '#f56565' : '#a0aec0'}
          stroke="#2d3748"
          strokeWidth="2"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedRegion('extremities')}
        />
        <rect
          x="155"
          y="280"
          width="25"
          height="100"
          rx="12"
          fill={assessmentData.extremities.status === 'normal' ? '#48bb78' : 
                assessmentData.extremities.status === 'abnormal' ? '#f56565' : '#a0aec0'}
          stroke="#2d3748"
          strokeWidth="2"
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedRegion('extremities')}
        />
        
        {/* Region Labels */}
        <text x="150" y="55" textAnchor="middle" fontSize="10" fill="#2d3748">HEAD</text>
        <text x="150" y="90" textAnchor="middle" fontSize="8" fill="#2d3748">NECK</text>
        <text x="150" y="160" textAnchor="middle" fontSize="10" fill="#2d3748">CHEST</text>
        <text x="150" y="255" textAnchor="middle" fontSize="10" fill="#2d3748">ABDOMEN</text>
        <text x="75" y="155" textAnchor="middle" fontSize="8" fill="#2d3748">ARM</text>
        <text x="225" y="155" textAnchor="middle" fontSize="8" fill="#2d3748">ARM</text>
        <text x="132" y="335" textAnchor="middle" fontSize="8" fill="#2d3748">LEG</text>
        <text x="167" y="335" textAnchor="middle" fontSize="8" fill="#2d3748">LEG</text>
      </svg>
      
      {/* Legend */}
      <Box position="absolute" top="2" right="2" bg="white" p="2" borderRadius="md" boxShadow="sm">
        <VStack spacing="1" align="flex-start">
          <HStack>
            <Box w="3" h="3" bg="green.500" borderRadius="full" />
            <Text fontSize="xs">Normal</Text>
          </HStack>
          <HStack>
            <Box w="3" h="3" bg="red.500" borderRadius="full" />
            <Text fontSize="xs">Abnormal</Text>
          </HStack>
          <HStack>
            <Box w="3" h="3" bg="gray.400" borderRadius="full" />
            <Text fontSize="xs">Not Assessed</Text>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );

  // Assessment panel for selected region
  const AssessmentPanel = ({ region }) => {
    const regionData = assessmentData[region];
    const regionNames = {
      heent: 'Head/HEENT',
      neck: 'Neck/Cervical',
      chest: 'Chest/Respiratory',
      abdomen: 'Abdomen',
      extremities: 'Extremities',
      neurological: 'Neurological',
      skin: 'Skin'
    };

    return (
      <Card>
        <CardHeader>
          <Heading size="md">{regionNames[region]} Assessment</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing="4" align="stretch">
            {/* Status Selection */}
            <FormControl>
              <FormLabel>Assessment Status</FormLabel>
              <ButtonGroup size="sm" isAttached>
                <Button
                  colorScheme={regionData.status === 'normal' ? 'green' : 'gray'}
                  onClick={() => updateAssessmentData(region, 'status', 'normal')}
                  leftIcon={<FaCheckCircle />}
                >
                  Normal
                </Button>
                <Button
                  colorScheme={regionData.status === 'abnormal' ? 'red' : 'gray'}
                  onClick={() => updateAssessmentData(region, 'status', 'abnormal')}
                  leftIcon={<FaTimesCircle />}
                >
                  Abnormal
                </Button>
                <Button
                  colorScheme={regionData.status === 'not_assessed' ? 'gray' : 'gray'}
                  onClick={() => updateAssessmentData(region, 'status', 'not_assessed')}
                  leftIcon={<FaQuestionCircle />}
                >
                  Not Assessed
                </Button>
              </ButtonGroup>
            </FormControl>

            {/* Severity Selection */}
            {regionData.status === 'abnormal' && (
              <FormControl>
                <FormLabel>Severity</FormLabel>
                <Select
                  value={regionData.severity}
                  onChange={(e) => updateAssessmentData(region, 'severity', e.target.value)}
                >
                  <option value="mild">Mild</option>
                  <option value="moderate">Moderate</option>
                  <option value="severe">Severe</option>
                </Select>
              </FormControl>
            )}

            {/* Findings */}
            <FormControl>
              <FormLabel>Findings & Notes</FormLabel>
              <Textarea
                value={regionData.findings}
                onChange={(e) => updateAssessmentData(region, 'findings', e.target.value)}
                placeholder="Enter assessment findings, observations, and notes..."
                rows={4}
              />
            </FormControl>

            {/* Region-specific details */}
            {region === 'heent' && (
              <Accordion allowToggle>
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      Detailed HEENT Assessment
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <Grid templateColumns="repeat(2, 1fr)" gap="4">
                      <FormControl>
                        <FormLabel>Eyes</FormLabel>
                        <Select
                          value={regionData.details.eyes}
                          onChange={(e) => updateAssessmentDetails(region, 'eyes', e.target.value)}
                        >
                          <option value="normal">Normal</option>
                          <option value="abnormal">Abnormal</option>
                          <option value="not_assessed">Not Assessed</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Ears</FormLabel>
                        <Select
                          value={regionData.details.ears}
                          onChange={(e) => updateAssessmentDetails(region, 'ears', e.target.value)}
                        >
                          <option value="normal">Normal</option>
                          <option value="abnormal">Abnormal</option>
                          <option value="not_assessed">Not Assessed</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Nose</FormLabel>
                        <Select
                          value={regionData.details.nose}
                          onChange={(e) => updateAssessmentDetails(region, 'nose', e.target.value)}
                        >
                          <option value="normal">Normal</option>
                          <option value="abnormal">Abnormal</option>
                          <option value="not_assessed">Not Assessed</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Throat</FormLabel>
                        <Select
                          value={regionData.details.throat}
                          onChange={(e) => updateAssessmentDetails(region, 'throat', e.target.value)}
                        >
                          <option value="normal">Normal</option>
                          <option value="abnormal">Abnormal</option>
                          <option value="not_assessed">Not Assessed</option>
                        </Select>
                      </FormControl>
                    </Grid>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )}

            {region === 'chest' && (
              <Accordion allowToggle>
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      Detailed Chest Assessment
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <Grid templateColumns="repeat(2, 1fr)" gap="4">
                      <FormControl>
                        <FormLabel>Heart Sounds</FormLabel>
                        <Select
                          value={regionData.details.heartSounds}
                          onChange={(e) => updateAssessmentDetails(region, 'heartSounds', e.target.value)}
                        >
                          <option value="normal">Normal</option>
                          <option value="abnormal">Abnormal</option>
                          <option value="not_assessed">Not Assessed</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Lung Sounds</FormLabel>
                        <Select
                          value={regionData.details.lungSounds}
                          onChange={(e) => updateAssessmentDetails(region, 'lungSounds', e.target.value)}
                        >
                          <option value="normal">Normal</option>
                          <option value="abnormal">Abnormal</option>
                          <option value="not_assessed">Not Assessed</option>
                        </Select>
                      </FormControl>
                      <FormControl>
                        <FormLabel>Breathing</FormLabel>
                        <Select
                          value={regionData.details.breathing}
                          onChange={(e) => updateAssessmentDetails(region, 'breathing', e.target.value)}
                        >
                          <option value="normal">Normal</option>
                          <option value="abnormal">Abnormal</option>
                          <option value="not_assessed">Not Assessed</option>
                        </Select>
                      </FormControl>
                    </Grid>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )}

            {region === 'abdomen' && (
              <Accordion allowToggle>
                <AccordionItem>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      Detailed Abdominal Assessment
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel>
                    <VStack spacing="4">
                      <Text fontWeight="bold">Abdominal Quadrants</Text>
                      <Grid templateColumns="repeat(2, 1fr)" gap="4">
                        <FormControl>
                          <FormLabel>RUQ</FormLabel>
                          <Select
                            value={regionData.details.quadrants.ruq}
                            onChange={(e) => updateAssessmentDetails(region, 'quadrants', {
                              ...regionData.details.quadrants,
                              ruq: e.target.value
                            })}
                          >
                            <option value="normal">Normal</option>
                            <option value="abnormal">Abnormal</option>
                            <option value="not_assessed">Not Assessed</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>LUQ</FormLabel>
                          <Select
                            value={regionData.details.quadrants.luq}
                            onChange={(e) => updateAssessmentDetails(region, 'quadrants', {
                              ...regionData.details.quadrants,
                              luq: e.target.value
                            })}
                          >
                            <option value="normal">Normal</option>
                            <option value="abnormal">Abnormal</option>
                            <option value="not_assessed">Not Assessed</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>RLQ</FormLabel>
                          <Select
                            value={regionData.details.quadrants.rlq}
                            onChange={(e) => updateAssessmentDetails(region, 'quadrants', {
                              ...regionData.details.quadrants,
                              rlq: e.target.value
                            })}
                          >
                            <option value="normal">Normal</option>
                            <option value="abnormal">Abnormal</option>
                            <option value="not_assessed">Not Assessed</option>
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>LLQ</FormLabel>
                          <Select
                            value={regionData.details.quadrants.llq}
                            onChange={(e) => updateAssessmentDetails(region, 'quadrants', {
                              ...regionData.details.quadrants,
                              llq: e.target.value
                            })}
                          >
                            <option value="normal">Normal</option>
                            <option value="abnormal">Abnormal</option>
                            <option value="not_assessed">Not Assessed</option>
                          </Select>
                        </FormControl>
                      </Grid>
                      <FormControl>
                        <FormLabel>Bowel Sounds</FormLabel>
                        <Select
                          value={regionData.details.bowelSounds}
                          onChange={(e) => updateAssessmentDetails(region, 'bowelSounds', e.target.value)}
                        >
                          <option value="normal">Normal</option>
                          <option value="abnormal">Abnormal</option>
                          <option value="not_assessed">Not Assessed</option>
                        </Select>
                      </FormControl>
                    </VStack>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            )}
          </VStack>
        </CardBody>
      </Card>
    );
  };

  // Vital signs panel
  const VitalSignsPanel = () => (
    <Card>
      <CardHeader>
        <Heading size="md">Vital Signs</Heading>
      </CardHeader>
      <CardBody>
        <Grid templateColumns="repeat(2, 1fr)" gap="4">
          <FormControl>
            <FormLabel>Heart Rate (BPM)</FormLabel>
            <Input
              type="number"
              value={vitalSigns.heartRate}
              onChange={(e) => setVitalSigns(prev => ({ ...prev, heartRate: e.target.value }))}
              placeholder="60-100"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Blood Pressure</FormLabel>
            <Input
              value={vitalSigns.bloodPressure}
              onChange={(e) => setVitalSigns(prev => ({ ...prev, bloodPressure: e.target.value }))}
              placeholder="120/80"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Respiratory Rate</FormLabel>
            <Input
              type="number"
              value={vitalSigns.respiratoryRate}
              onChange={(e) => setVitalSigns(prev => ({ ...prev, respiratoryRate: e.target.value }))}
              placeholder="12-20"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Temperature (°F)</FormLabel>
            <Input
              type="number"
              step="0.1"
              value={vitalSigns.temperature}
              onChange={(e) => setVitalSigns(prev => ({ ...prev, temperature: e.target.value }))}
              placeholder="98.6"
            />
          </FormControl>
          <FormControl>
            <FormLabel>O2 Saturation (%)</FormLabel>
            <Input
              type="number"
              value={vitalSigns.oxygenSaturation}
              onChange={(e) => setVitalSigns(prev => ({ ...prev, oxygenSaturation: e.target.value }))}
              placeholder="95-100"
            />
          </FormControl>
        </Grid>
      </CardBody>
    </Card>
  );

  // Quick assessment summary
  const AssessmentSummary = () => {
    const assessmentCounts = Object.entries(assessmentData).reduce((acc, [region, data]) => {
      acc[data.status] = (acc[data.status] || 0) + 1;
      return acc;
    }, {});

    return (
      <Card>
        <CardHeader>
          <Heading size="md">Assessment Summary</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing="4">
            <HStack spacing="4" justify="center">
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="green.500">
                  {assessmentCounts.normal || 0}
                </Text>
                <Text fontSize="sm">Normal</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="red.500">
                  {assessmentCounts.abnormal || 0}
                </Text>
                <Text fontSize="sm">Abnormal</Text>
              </VStack>
              <VStack>
                <Text fontSize="2xl" fontWeight="bold" color="gray.500">
                  {assessmentCounts.not_assessed || 0}
                </Text>
                <Text fontSize="sm">Not Assessed</Text>
              </VStack>
            </HStack>
            
            <Divider />
            
            <VStack spacing="2" align="stretch">
              <Text fontWeight="bold">Quick Status Overview:</Text>
              {Object.entries(assessmentData).map(([region, data]) => {
                const regionNames = {
                  heent: 'Head/HEENT',
                  neck: 'Neck/Cervical',
                  chest: 'Chest/Respiratory',
                  abdomen: 'Abdomen',
                  extremities: 'Extremities',
                  neurological: 'Neurological',
                  skin: 'Skin'
                };
                
                return (
                  <HStack key={region} justify="space-between">
                    <Text fontSize="sm">{regionNames[region]}</Text>
                    <Badge
                      colorScheme={
                        data.status === 'normal' ? 'green' :
                        data.status === 'abnormal' ? 'red' : 'gray'
                      }
                    >
                      {data.status.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </HStack>
                );
              })}
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader>
          <HStack>
            <Text>Interactive Patient Assessment</Text>
            {patientEncounter && (
              <Badge colorScheme="blue">
                Patient ID: {patientEncounter.id}
              </Badge>
            )}
            {isSaving && (
              <Badge colorScheme="yellow" variant="subtle">
                Saving...
              </Badge>
            )}
            {lastSaved && !isSaving && (
              <Badge colorScheme="green" variant="subtle">
                Saved {lastSaved.toLocaleTimeString()}
              </Badge>
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <Tabs variant="enclosed">
            <TabList>
              <Tab>
                <HStack>
                  <FaHandPaper />
                  <Text>Body Assessment</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack>
                  <FaHeart />
                  <Text>Vital Signs</Text>
                </HStack>
              </Tab>
              <Tab>
                <HStack>
                  <FaCheckCircle />
                  <Text>Summary</Text>
                </HStack>
              </Tab>
            </TabList>

            <TabPanels>
              {/* Body Assessment Tab */}
              <TabPanel>
                <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap="6">
                  <GridItem>
                    <VStack spacing="4">
                      <Text fontWeight="bold" fontSize="lg">
                        Select Body Region
                      </Text>
                      <HumanBodyDiagram />
                      
                      {/* Region selection buttons */}
                      <Box w="100%">
                        <Text fontSize="sm" mb="2">Quick Region Selection:</Text>
                        <Grid templateColumns="repeat(2, 1fr)" gap="2">
                          {Object.entries(assessmentData).map(([region, data]) => {
                            const regionNames = {
                              heent: 'Head/HEENT',
                              neck: 'Neck',
                              chest: 'Chest',
                              abdomen: 'Abdomen',
                              extremities: 'Extremities',
                              neurological: 'Neurological',
                              skin: 'Skin'
                            };
                            
                            const regionIcons = {
                              heent: FaEye,
                              neck: FaHandPaper,
                              chest: FaLungs,
                              abdomen: FaHandPaper,
                              extremities: FaHandPaper,
                              neurological: FaBrain,
                              skin: FaHandPaper
                            };
                            
                            const IconComponent = regionIcons[region];
                            
                            return (
                              <Button
                                key={region}
                                size="sm"
                                variant={selectedRegion === region ? 'solid' : 'outline'}
                                colorScheme={
                                  data.status === 'normal' ? 'green' :
                                  data.status === 'abnormal' ? 'red' : 'gray'
                                }
                                leftIcon={<IconComponent />}
                                onClick={() => setSelectedRegion(region)}
                              >
                                {regionNames[region]}
                              </Button>
                            );
                          })}
                        </Grid>
                      </Box>
                    </VStack>
                  </GridItem>
                  
                  <GridItem>
                    <AssessmentPanel region={selectedRegion} />
                  </GridItem>
                </Grid>
              </TabPanel>
              
              {/* Vital Signs Tab */}
              <TabPanel>
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="6">
                  <GridItem>
                    <VitalSignsPanel />
                  </GridItem>
                  <GridItem>
                    <Card>
                      <CardHeader>
                        <Heading size="md">Vital Signs Reference</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing="3" align="stretch">
                          <Box>
                            <Text fontWeight="bold" fontSize="sm">Heart Rate</Text>
                            <Text fontSize="xs" color="gray.600">
                              Adult: 60-100 bpm | Child: 80-120 bpm
                            </Text>
                          </Box>
                          <Box>
                            <Text fontWeight="bold" fontSize="sm">Blood Pressure</Text>
                            <Text fontSize="xs" color="gray.600">
                              Normal: {'<120/80'} | High: {'> 140/90'}
                            </Text>
                          </Box>
                          <Box>
                            <Text fontWeight="bold" fontSize="sm">Respiratory Rate</Text>
                            <Text fontSize="xs" color="gray.600">
                              Adult: 12-20 rpm | Child: 16-24 rpm
                            </Text>
                          </Box>
                          <Box>
                            <Text fontWeight="bold" fontSize="sm">Temperature</Text>
                            <Text fontSize="xs" color="gray.600">
                              Normal: 97.8-99.1°F (36.5-37.3°C)
                            </Text>
                          </Box>
                          <Box>
                            <Text fontWeight="bold" fontSize="sm">O2 Saturation</Text>
                            <Text fontSize="xs" color="gray.600">
                              Normal: 95-100% | Critical: {'<90%'}
                            </Text>
                          </Box>
                        </VStack>
                      </CardBody>
                    </Card>
                  </GridItem>
                </Grid>
              </TabPanel>
              
              {/* Summary Tab */}
              <TabPanel>
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="6">
                  <GridItem>
                    <AssessmentSummary />
                  </GridItem>
                  <GridItem>
                    <Card>
                      <CardHeader>
                        <Heading size="md">Assessment Notes</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing="4" align="stretch">
                          {Object.entries(assessmentData)
                            .filter(([region, data]) => data.findings)
                            .map(([region, data]) => {
                              const regionNames = {
                                heent: 'Head/HEENT',
                                neck: 'Neck/Cervical',
                                chest: 'Chest/Respiratory',
                                abdomen: 'Abdomen',
                                extremities: 'Extremities',
                                neurological: 'Neurological',
                                skin: 'Skin'
                              };
                              
                              return (
                                <Box key={region} p="3" borderRadius="md" bg="gray.50">
                                  <Text fontWeight="bold" fontSize="sm" mb="1">
                                    {regionNames[region]}
                                  </Text>
                                  <Text fontSize="xs">{data.findings}</Text>
                                </Box>
                              );
                            })}
                          {Object.entries(assessmentData).every(([region, data]) => !data.findings) && (
                            <Text fontSize="sm" color="gray.500" textAlign="center">
                              No assessment notes recorded yet.
                            </Text>
                          )}
                        </VStack>
                      </CardBody>
                    </Card>
                  </GridItem>
                </Grid>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
        
        <ModalFooter>
          <HStack spacing="3">
            <Button
              leftIcon={<FaExclamationTriangle />}
              variant="ghost"
              size="sm"
              onClick={() => {
                toast({
                  title: 'Assessment Help',
                  description: 'Click on body regions to assess them. Use the tabs to navigate between assessment areas.',
                  status: 'info',
                  duration: 5000,
                  isClosable: true,
                });
              }}
            >
              Help
            </Button>
            
            <Spacer />
            
            {onExport && (
              <Button
                leftIcon={<FaDownload />}
                variant="outline"
                onClick={handleExport}
                size="sm"
              >
                Export
              </Button>
            )}
            
            <Button onClick={onClose} size="sm">
              Close
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default InteractivePatientAssessment;